import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db, collection, getDocs, query, where } from '../../firebase/config';
import { ChevronLeft, Star, MapPin, User, Search, Clock, Sparkles } from 'lucide-react';

const DiscoverCategoryPage = () => {
  const navigate = useNavigate();
  const { category } = useParams();

  const [services, setServices] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const categoryTitles = {
    all: 'All Services',
    hair: 'Hair Stylists',
    nails: 'Nail Artists',
    skin: 'Skin Experts',
    lashes: 'Lash Techs',
    spa: 'Spa & Massage',
    makeup: 'Makeup Artists',
  };

  const getCover = (service) => {
    if (Array.isArray(service.media) && service.media.length > 0) return service.media[0];
    if (service.media?.url) return service.media;
    if (service.mediaLegacy?.url) return service.mediaLegacy;
    return null;
  };

  const serviceMatchesCategory = (service, cat) => {
    if (cat === 'all' || !cat) return true;
    const spec = (service.specialization || '').toLowerCase();
    const catField = (service.category || '').toLowerCase();
    const title = (service.title || '').toLowerCase();
    const haystack = `${spec} ${catField} ${title}`;

    if (cat === 'hair') return haystack.includes('hair') || haystack.includes('stylist') || haystack.includes('cut') || haystack.includes('color') || haystack.includes('balayage');
    if (cat === 'nails') return haystack.includes('nail') || haystack.includes('manicure') || haystack.includes('pedicure') || haystack.includes('gel') || haystack.includes('acrylic');
    if (cat === 'skin') return haystack.includes('skin') || haystack.includes('esthetician') || haystack.includes('facial') || haystack.includes('peel') || haystack.includes('dermaplaning');
    if (cat === 'lashes') return haystack.includes('lash') || haystack.includes('brow') || haystack.includes('volume') || haystack.includes('classic');
    if (cat === 'spa') return haystack.includes('spa') || haystack.includes('massage') || haystack.includes('swedish') || haystack.includes('deep tissue') || haystack.includes('hot stone');
    if (cat === 'makeup') return haystack.includes('makeup') || haystack.includes('mua') || haystack.includes('bridal');
    return haystack.includes(cat);
  };

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      const cat = (category || 'all').toLowerCase();

      try {
        // 1. Fetch NEW Root Services and Professional Users
        const [servicesSnapshot, usersSnapshot] = await Promise.all([
          getDocs(collection(db, 'services')),
          getDocs(query(collection(db, 'users'), where('role', '==', 'professional'))),
        ]);

        if (cancelled) return;

        let allServicesData = servicesSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

        const prosData = usersSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        const prosMap = {};
        prosData.forEach(p => { prosMap[p.id] = p; });

        // 2. Fetch OLD Legacy Services (from /professionals/{id}/services)
        try {
          const legacyPromises = prosData.map(pro =>
            getDocs(collection(db, 'professionals', pro.id, 'services'))
              .then(snap => {
                if (snap && snap.docs) {
                  snap.docs.forEach(doc => {
                    const data = doc.data();
                    allServicesData.push({
                      id: doc.id,
                      ...data,
                      // Ensure old services map to the right pro even if they lacked the professionalId field
                      professionalId: data.professionalId || pro.id, 
                      isLegacy: true
                    });
                  });
                }
              })
              .catch(() => null) // Silently ignore if folder doesn't exist
          );
          await Promise.all(legacyPromises);
        } catch (err) {
          console.warn('Error fetching legacy services:', err);
        }

        // 3. Filter Active and Enrich Data
        allServicesData = allServicesData.filter(s => s.active !== false);

        const enriched = allServicesData
          .map(s => {
            const pro = prosMap[s.professionalId] || {};
            return {
              ...s,
              specialization: s.specialization || pro.specialization || '',
              specializations: s.specializations || pro.specializations || [],
              category: s.category || '',
              pro: {
                id: s.professionalId,
                name: pro.businessName || pro.name || 'Professional',
                image: pro.profileImage || pro.coverImage || null,
                location: pro.location || '',
                rating: Number(pro.rating || 0),
                ratingCount: Number(pro.ratingCount || 0),
              },
            };
          })
          .filter(s => serviceMatchesCategory(s, cat));

        setServices(enriched);
      } catch (error) {
        console.error('Error loading services:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();
    return () => { cancelled = true; };
  }, [category]);

  // Derived filter — does NOT change input focus because it's memoized
  const filteredServices = useMemo(() => {
    if (!searchQuery.trim()) return services;
    const s = searchQuery.toLowerCase();
    return services.filter(sv =>
      (sv.title || '').toLowerCase().includes(s) ||
      (sv.category || '').toLowerCase().includes(s) ||
      (sv.pro?.name || '').toLowerCase().includes(s) ||
      (sv.pro?.location || '').toLowerCase().includes(s)
    );
  }, [searchQuery, services]);

  // Safe navigation handler
  const handleServiceClick = (service) => {
    if (service.pro?.id) {
      navigate(`/professional/${service.pro.id}?service=${service.id}`);
    } else {
      console.warn('Cannot open service: missing professionalId', service);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-50 rounded-full transition-colors"
        >
          <ChevronLeft size={24} />
        </button>
        <div>
          <h1 className="text-3xl font-serif font-bold text-gray-900">
            {categoryTitles[category] || 'Services'}
          </h1>
          <p className="text-gray-500 mt-1">
            {loading
              ? 'Loading...'
              : `${filteredServices.length} service${filteredServices.length === 1 ? '' : 's'} available`}
          </p>
        </div>
      </div>

      {/* Search — ALWAYS rendered (not gated by loading or services.length) */}
      <div className="relative mb-6">
        <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search services, categories, or pros..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-100 rounded-2xl text-sm focus:outline-none focus:border-[#EFA9B6]"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-12 h-12 border-4 border-[#EFA9B6] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : services.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-3xl border border-gray-50">
          <div className="w-20 h-20 bg-[#FFF5F6] rounded-full flex items-center justify-center mx-auto mb-4">
            <Sparkles size={32} className="text-[#EFA9B6]" />
          </div>
          <h3 className="text-lg font-serif font-bold text-gray-900 mb-2">
            No {categoryTitles[category] || 'services'} found
          </h3>
          <p className="text-gray-400 text-sm">Check back later</p>
        </div>
      ) : filteredServices.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-3xl border border-gray-50">
          <div className="w-20 h-20 bg-[#FFF5F6] rounded-full flex items-center justify-center mx-auto mb-4">
            <Search size={32} className="text-[#EFA9B6]" />
          </div>
          <h3 className="text-lg font-serif font-bold text-gray-900 mb-2">No results</h3>
          <p className="text-gray-400 text-sm">Try a different search term</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredServices.map((service) => {
            const cover = getCover(service);
            const priceFrom = Array.isArray(service.options) && service.options.length > 0
              ? Math.min(...service.options.map(o => Number(o.price) || 0))
              : Number(service.price || 0);

            return (
              <div
                key={service.id}
                onClick={() => handleServiceClick(service)}
                className="bg-white rounded-[2rem] border border-gray-100 hover:border-[#EFA9B6] transition-colors cursor-pointer group overflow-hidden flex flex-col"
              >
                <div className="w-full aspect-[4/3] bg-[#FFF5F6] overflow-hidden shrink-0">
                  {cover ? (
                    cover.type === 'video' ? (
                      <video src={cover.url} className="w-full h-full object-cover" muted playsInline />
                    ) : (
                      <img
                        src={cover.url}
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    )
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Sparkles size={40} className="text-[#EFA9B6]" />
                    </div>
                  )}
                </div>

                <div className="p-4 flex-1 flex flex-col">
                  {service.category && (
                    <span className="text-[10px] font-bold tracking-wider text-[#EFA9B6] uppercase mb-1">
                      {service.category}
                    </span>
                  )}

                  <h3 className="font-serif font-bold text-gray-900 text-base mb-1 truncate">
                    {service.title}
                  </h3>

                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full bg-[#FFF5F6] flex items-center justify-center overflow-hidden shrink-0">
                      {service.pro.image ? (
                        <img src={service.pro.image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User size={12} className="text-[#EFA9B6]" />
                      )}
                    </div>
                    <span className="text-xs text-gray-600 truncate">
                      {service.pro.name}
                    </span>
                  </div>

                  <div className="flex items-center gap-3 text-[11px] text-gray-500 mb-3 flex-wrap">
                    {service.duration && (
                      <span className="flex items-center gap-1">
                        <Clock size={11} />
                        {service.duration}
                      </span>
                    )}
                    {service.pro.rating > 0 && (
                      <span className="flex items-center gap-1">
                        <Star size={11} className="fill-amber-400 text-amber-400" />
                        <span className="font-bold text-gray-900">{service.pro.rating.toFixed(1)}</span>
                        {service.pro.ratingCount > 0 && (
                          <span className="text-gray-400">({service.pro.ratingCount})</span>
                        )}
                      </span>
                    )}
                  </div>

                  <div className="mt-auto pt-3 border-t border-gray-50 flex items-end justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider">
                        {service.variablePrice || (Array.isArray(service.options) && service.options.length > 0)
                          ? 'From'
                          : 'Price'}
                      </p>
                      <p className="font-bold text-gray-900 text-base">
                        R{priceFrom.toFixed(0)}
                      </p>
                    </div>
                    {service.pro.location && (
                      <div className="flex items-center gap-1 text-[11px] text-gray-400 min-w-0 justify-end">
                        <MapPin size={11} className="shrink-0" />
                        <span className="truncate">{service.pro.location}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DiscoverCategoryPage;